import React, { useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomButton from '../../../../../components/buttons/CustomButton';
import Toast from '../../../../../components/support/Toast';
import ConfirmPopup from '../../../../../popup/ConfirmPopup/ConfirmPopup';
import InsuranceDocumentAttachmentsPopup from '../../../../../components/insurance-documents/InsuranceDocumentAttachmentsPopup';
import {
  MEDICAL_REQUEST_CONTEXT, INSURANCE_AUTHORIZATION_CONTEXT,
} from '../../../../../components/insurance-documents/InsuranceDocumentAttachments';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import ModuleEmptyState from '../../../../appointment/components/moduleCard/ModuleEmptyState';
import { DocumentsBlock, DocumentsSkeleton } from './DocumentsBlock';
import MedicalRequestRow from './MedicalRequestRow';
import AuthorizationRow from './AuthorizationRow';
import MedicalRequestFormPopup from './MedicalRequestFormPopup';
import AuthorizationFormPopup from './AuthorizationFormPopup';
import useInsuranceDocuments from './useInsuranceDocuments';
import { referenceLabel } from './documentFormat';
import styles from './InsuranceDocumentsSection.module.css';

const MANUAL_STATUS_NOTICE = 'A situação é registrada manualmente. Cadastrar o número não quer dizer que a operadora autorizou.';

const isInactive = (item) => item.active === 0 || item.active === false;

/**
 * Pedidos médicos e guias/autorizações da pessoa (Fase 2 de convênios), com
 * anexos por documento. Dois blocos no padrão das áreas da ficha; a casca
 * (título da aba, carteirinhas) fica com quem monta a seção Convênios.
 */
export default function InsuranceDocumentsSection({ patientId, insurances }) {
  const { CompanyConfig } = DataProviderInstance();
  const providers = CompanyConfig?.InsuranceProviders || [];
  const docs = useInsuranceDocuments(patientId);

  // { request: null | objeto } abre o formulário de pedido; idem para a guia
  const [requestForm, setRequestForm] = useState(null);
  const [authorizationForm, setAuthorizationForm] = useState(null);
  const [inactivate, setInactivate] = useState(null); // { kind, item }
  const [attachments, setAttachments] = useState(null); // { context, contextId, subject }

  const newRequestButton = (
    <CustomButton style="main" variant="contained" icon={<AddRoundedIcon />} label="Novo pedido" onClick={() => setRequestForm({ request: null })} />
  );
  const newAuthorizationButton = (
    <CustomButton style="main" variant="contained" icon={<AddRoundedIcon />} label="Nova guia" onClick={() => setAuthorizationForm({ authorization: null })} />
  );

  const toggleRequest = (request) => {
    if (isInactive(request)) docs.setRequestActive(request, true);
    else setInactivate({ kind: 'request', item: request });
  };

  const toggleAuthorization = (authorization) => {
    if (isInactive(authorization)) docs.setAuthorizationActive(authorization, true);
    else setInactivate({ kind: 'authorization', item: authorization });
  };

  const confirmInactivate = () => {
    if (!inactivate) return;
    if (inactivate.kind === 'request') docs.setRequestActive(inactivate.item, false);
    else docs.setAuthorizationActive(inactivate.item, false);
  };

  const openRequestAttachments = (request) => setAttachments({
    context: MEDICAL_REQUEST_CONTEXT, contextId: request.id, subject: request.title,
  });
  const openAuthorizationAttachments = (authorization) => setAttachments({
    context: INSURANCE_AUTHORIZATION_CONTEXT, contextId: authorization.id, subject: referenceLabel(authorization),
  });

  const inactivateTexts = inactivate?.kind === 'request'
    ? { title: 'Inativar pedido', text: `O pedido "${inactivate.item.title}" sai das opções ao ligar uma guia ou um atendimento, mas o histórico continua. Dá para reativar depois.` }
    : { title: 'Inativar guia', text: `A guia "${inactivate ? referenceLabel(inactivate.item) : ''}" sai das opções ao marcar um atendimento, mas o histórico continua. Dá para reativar depois.` };

  return (
    <div className={styles.area}>
      {docs.loadError && <div className={styles.error} role="alert">{docs.loadError}</div>}

      <DocumentsBlock
        title="Pedidos médicos"
        hint="Pedido de quem indicou o atendimento, com vencimento e anexos"
        icon={<DescriptionOutlinedIcon />}
        accent="purple"
        actions={newRequestButton}
      >
        {docs.loading ? (
          <DocumentsSkeleton rows={2} />
        ) : docs.requests.length === 0 ? (
          <ModuleEmptyState icon={<DescriptionOutlinedIcon />} text="Nenhum pedido médico registrado." />
        ) : (
          <div className={styles.card}>
            <ul className={styles.list}>
              {docs.requests.map((request) => (
                <MedicalRequestRow
                  key={request.id}
                  request={request}
                  busy={docs.busy}
                  onEdit={(item) => setRequestForm({ request: item })}
                  onToggle={toggleRequest}
                  onAttachments={openRequestAttachments}
                />
              ))}
            </ul>
          </div>
        )}
      </DocumentsBlock>

      <DocumentsBlock
        title="Guias e autorizações"
        hint="Número, senha e situação da autorização da operadora"
        icon={<AssignmentTurnedInOutlinedIcon />}
        accent="blue"
        actions={newAuthorizationButton}
      >
        {docs.loading ? (
          <DocumentsSkeleton rows={2} />
        ) : docs.authorizations.length === 0 ? (
          <ModuleEmptyState icon={<AssignmentTurnedInOutlinedIcon />} text="Nenhuma guia registrada." />
        ) : (
          <div className={styles.card}>
            <ul className={styles.list}>
              {docs.authorizations.map((authorization) => (
                <AuthorizationRow
                  key={authorization.id}
                  authorization={authorization}
                  busy={docs.busy}
                  onEdit={(item) => setAuthorizationForm({ authorization: item })}
                  onToggle={toggleAuthorization}
                  onStatusChange={docs.changeAuthorizationStatus}
                  onAttachments={openAuthorizationAttachments}
                />
              ))}
            </ul>
          </div>
        )}
        <div className={styles.notice}>
          <InfoOutlinedIcon className={styles.noticeIcon} />
          <span>{MANUAL_STATUS_NOTICE}</span>
        </div>
      </DocumentsBlock>

      {requestForm && (
        <MedicalRequestFormPopup
          request={requestForm.request}
          patientId={patientId}
          onSave={(values) => docs.saveRequest(values, requestForm.request)}
          onSaved={docs.reload}
          onClose={() => setRequestForm(null)}
        />
      )}

      {authorizationForm && (
        <AuthorizationFormPopup
          authorization={authorizationForm.authorization}
          insurances={insurances}
          providers={providers}
          requests={docs.requests}
          patientId={patientId}
          onSave={(values) => docs.saveAuthorization(values, authorizationForm.authorization)}
          onSaved={docs.reload}
          onClose={() => setAuthorizationForm(null)}
        />
      )}

      {attachments && (
        <InsuranceDocumentAttachmentsPopup
          patientId={patientId}
          context={attachments.context}
          contextId={attachments.contextId}
          subject={attachments.subject}
          onClose={(changed) => {
            setAttachments(null);
            if (changed) docs.reload();
          }}
        />
      )}

      <ConfirmPopup
        Visibility={!!inactivate}
        Title={inactivateTexts.title}
        Description={inactivateTexts.text}
        ConfirmLabel="Inativar"
        OnConfirm={confirmInactivate}
        OnHide={() => setInactivate(null)}
      />

      <Toast
        open={docs.toast.open}
        onClose={docs.closeToast}
        message={docs.toast.message}
        severity={docs.toast.severity}
        autoHideDuration={5000}
      />
    </div>
  );
}
